import React, { useState } from 'react';
import { CalendarRange, RefreshCw } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import { navLabel } from '../utils/navigation';
import FlowTabs from '../components/flow/FlowTabs';
import QuarterView from '../components/planning/QuarterView';
import SprintView from '../components/planning/SprintView';
import { projectParam, quarterParam } from '../utils/urlParams';
import { useTabParam } from '../hooks/useTabParam';
import { useUrlState } from '../hooks/useUrlState';

type TabId = 'quarter' | 'sprint';

// Planning: how far each quarter's goal epics have come, and how each sprint went. Delivery
// reporting, so every role reads it. The project picked on either tab carries to the other.
export default function PlanningPage() {
  // The tab, the quarter and the project live in the address, so a reload or a copied link opens the same
  // view (and a link from the Compliance page can open a quarter and a project).
  const { params, update } = useUrlState();
  const [tab, setTab] = useTabParam<TabId>(['quarter', 'sprint'], 'quarter');
  const quarterId = quarterParam(params);
  const setQuarterId = (id: string) => update({ quarter: id });
  const projectKey = projectParam(params);
  const setProjectKey = (key: string | null) => update({ project_key: key });
  const [refreshKey, setRefreshKey] = useState(0);

  const tabs = [
    { id: 'quarter', label: 'Quarter' },
    { id: 'sprint', label: 'Sprint', tip: 'planning.sprint' as const },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100/60">
      <PageHeader title={navLabel('/planning')} subtitle="How far each quarter has come, and how each sprint went" icon={CalendarRange} />
      <div className="p-4 sm:p-6">
        <div className="mx-auto max-w-7xl">
          <div className="flex items-end justify-between gap-3">
            <div className="min-w-0 flex-1">
              <FlowTabs tabs={tabs} active={tab} onChange={(id) => setTab(id as TabId)} label="Planning views" idPrefix="planning" />
            </div>
            <button
              type="button"
              onClick={() => setRefreshKey((n) => n + 1)}
              aria-label="Refresh"
              className="mb-5 rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-white hover:text-gray-900 active:scale-[0.96] motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
          </div>

          {tab === 'quarter' ? (
            <div role="tabpanel" id="planning-panel-quarter" aria-labelledby="planning-tab-quarter">
              <QuarterView quarterId={quarterId} onQuarter={setQuarterId} projectKey={projectKey} onProject={setProjectKey} refreshKey={refreshKey} />
            </div>
          ) : (
            <div role="tabpanel" id="planning-panel-sprint" aria-labelledby="planning-tab-sprint">
              <SprintView projectKey={projectKey} refreshKey={refreshKey} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
